import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    Button,
    TextField,
    Box,
    CircularProgress,
} from "@mui/material";
import { useGetSubscription, useUpdateSubscription } from "../../api/hooks/useSubscription";
import { showSuccess, showError } from "../../helpers/toast";
import type { UpdateSubscriptionPayload } from "../../api/types/subscription.types";
import { isFloat } from "../../helpers/validations";

const EditSubscription = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const subscriptionId = location?.state?.subscriptionId;

    const [price, setPrice] = useState<string>("0");
    const [benefits, setBenefits] = useState<string>("");
    const [errors, setErrors] = useState<Record<string, string>>({});

    // Fetch subscription data
    const { data: subscriptionData, isLoading: isLoadingSubscription, error: subscriptionError } = useGetSubscription(
        subscriptionId || '',
        { enabled: !!subscriptionId }
    );

    // Update form fields when subscription data is loaded
    useEffect(() => {
        if (subscriptionData?.data) {
            const subscription = subscriptionData.data;
            setPrice(subscription.price?.toString() || "0");
            setBenefits(subscription.benefits || "");
        }
    }, [subscriptionData]);

    const formatType = (type: number) => {
        switch (type) {
            case 1:
                return "Free";
            case 2:
                return "Premium";
            default:
                return "N/A";
        }
    };

    const formatValidity = (validity: number) => {
        if (!validity) return "N/A";
        if (validity === 365) return "1 Year";
        if (validity === 30) return "1 Month";
        if (validity < 30) return `${validity} Days`;
        if (validity < 365) {
            const months = Math.floor(validity / 30);
            return `${months} Month${months > 1 ? 's' : ''}`;
        }
        const years = Math.floor(validity / 365);
        return `${years} Year${years > 1 ? 's' : ''}`;
    };

    // Handle error
    useEffect(() => {
        if (subscriptionError) {
            showError((subscriptionError as any)?.response?.data?.message || "Failed to load subscription details");
        }
    }, [subscriptionError]);

    useEffect(() => {
        if (!subscriptionId && !isLoadingSubscription) {
            showError("No subscription selected. Redirecting to subscription list.");
            navigate("/subscription");
        }
    }, [subscriptionId, isLoadingSubscription, navigate]);

    // Update subscription mutation
    const { mutate: updateSubscription, isPending: isUpdating } = useUpdateSubscription({
        onSuccess: (response) => {
            showSuccess(response.message || "Subscription updated successfully");
            navigate("/subscription");
        },
        onError: (error: any) => {
            showError(error?.response?.data?.message || "Failed to update subscription");
        },
    });

    // Validation
    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!price.trim()) {
            newErrors.price = "Price is required";
        } else {
            const numPrice = Number(price);
            if (isNaN(numPrice) || numPrice < 0) {
                newErrors.price = "Please enter a valid price (0 or greater)";
            }
        }

        if (!benefits.trim()) {
            newErrors.benefits = "Benefits are required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!validate()) {
            return;
        }

        const payload: UpdateSubscriptionPayload = {
            price: Number(price),
            benefits: benefits.trim(),
        };

        updateSubscription({ id: subscriptionId, data: payload });
    };

    if (isLoadingSubscription) {
        return (
            <>
                <div className="main_title">
                    <h1>Edit Subscription</h1>
                    <p>
                        <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
                        <button onClick={() => navigate("/subscription")}>
                            Manage Subscriptions
                        </button>{" "}
                        - Edit Subscription
                    </p>
                </div>
                <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                    <CircularProgress />
                </Box>
            </>
        );
    }

    const isLoading = isUpdating;

    return (
        <>
            <div className="main_title">
                <h1>Edit Subscription</h1>
                <p>
                    <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
                    <button onClick={() => navigate("/subscription")}>
                        Manage Subscriptions
                    </button>{" "}
                    - Edit Subscription
                </p>
            </div>

            <div className="cards">
                <div className="form">
                    <form onSubmit={handleSubmit}>
                        <div className="gap_p">
                            {/* Subscription Type (Read-only) */}
                            <div className="control_group w_33">
                                <label htmlFor="subscription-type">Subscription Type</label>
                                <TextField
                                    id="subscription-type"
                                    value={subscriptionData?.data?.type ? formatType(subscriptionData.data.type) : "N/A"}
                                    fullWidth
                                    hiddenLabel
                                    disabled
                                />
                            </div>

                            {/* Subscription Name (Read-only) */}
                            <div className="control_group w_33">
                                <label htmlFor="subscription-name">Subscription Name</label>
                                <TextField
                                    id="subscription-name"
                                    value={subscriptionData?.data?.name || "N/A"}
                                    fullWidth
                                    hiddenLabel
                                    disabled
                                />
                            </div>

                            {/* Validity (Read-only) */}
                            <div className="control_group w_33">
                                <label htmlFor="validity">Validity</label>
                                <TextField
                                    id="validity"
                                    value={subscriptionData?.data?.validity ? formatValidity(subscriptionData.data.validity) : "N/A"}
                                    fullWidth
                                    hiddenLabel
                                    disabled
                                />
                            </div>

                            {/* Price */}
                            <div className="control_group w_33">
                                <label htmlFor="price">Price <span className="req">*</span></label>
                                <TextField
                                    id="price"
                                    type="text"
                                    placeholder="Enter Price (e.g. 9.99 or 0 for Free)"
                                    hiddenLabel
                                    fullWidth
                                    value={price}
                                    onChange={(e) => {
                                        if (isFloat(e.target.value) || e.target.value === "") {
                                            setPrice(e.target.value);
                                            if (errors.price) {
                                                setErrors({ ...errors, price: "" });
                                            }
                                        }
                                    }}
                                    error={!!errors.price}
                                    helperText={errors.price}
                                    required
                                    disabled={isLoading}
                                />
                            </div>

                            {/* Benefits */}
                            <div className="control_group w_100">
                                <label htmlFor="benefits">Benefits <span className="req">*</span></label>
                                <TextField
                                    id="benefits"
                                    placeholder="List benefits separated by commas or new lines"
                                    hiddenLabel
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    value={benefits}
                                    onChange={(e) => {
                                        setBenefits(e.target.value);
                                        if (errors.benefits) {
                                            setErrors({ ...errors, benefits: "" });
                                        }
                                    }}
                                    error={!!errors.benefits}
                                    helperText={errors.benefits}
                                    required
                                    disabled={isLoading}
                                />
                            </div>
                        </div>

                        <div className="form_btn">
                            <Button
                                variant="contained"
                                color="primary"
                                className="btn btn_primary"
                                type="submit"
                                disabled={isLoading}
                            >
                                {isLoading ? "Saving..." : "Save"}
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default EditSubscription;
